import {
  get,
  has,
  forEach,
  toLower,
  isEmpty,
  values,
  sortBy,
  find,
  filter,
} from "lodash";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RawGroup = {
  id: string;
  name: string;
  type: string;
  childTeams?: RawGroup[] | undefined;
  parentGroups?: { id: string; name: string }[] | undefined;
  [key: string]: unknown;
};

export type GroupTypeDef = {
  childTypes?: string[] | undefined;
  [key: string]: unknown;
};

export type GroupTypesLookup = Record<string, GroupTypeDef | undefined>;

/** Mutable tree node — children is a dict during build, array after convertChildrenToArray. */
export type GroupTreeNode = {
  id: string;
  name: string;
  group?: RawGroup | undefined;
  active?: boolean | undefined;
  toggled?: boolean | undefined;
  isParentType?: boolean | undefined;
  loading?: boolean | undefined;
  children: Record<string, GroupTreeNode> | GroupTreeNode[] | null;
  parentNode?: GroupTreeNode | VirtualRoot | undefined;
  [key: string]: unknown;
};

export type VirtualRoot = {
  active?: boolean | undefined;
  toggled?: boolean | undefined;
  children: Record<string, GroupTreeNode> | GroupTreeNode[] | null;
};

export type SortOption = "none" | "groupByType" | undefined;

export type TreeStructureResult = {
  root: VirtualRoot;
  treeData: GroupTreeNode[] | null;
  groups?: RawGroup[] | undefined;
  activeNode?: GroupTreeNode | null | undefined;
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export const updateTreeObject = <T>(tree: T): T => {
  if (Array.isArray(tree)) {
    return [...tree] as unknown as T;
  }
  return { ...(tree as object) } as T;
};

const byName = (g: GroupTreeNode) => toLower(get(g, "name") as string);
const byType = (g: GroupTreeNode) => toLower(get(g, "group.type") as string);
const isBranchExpanded = (n: GroupTreeNode): boolean =>
  !!(n.toggled || n.active);

export const canHaveChildren = (
  type: string,
  groupTypes: GroupTypesLookup
): boolean => {
  const groupType = get(groupTypes, type);

  if (!groupType || !has(groupType, "childTypes")) {
    return true;
  }

  return !isEmpty(get(groupType, "childTypes"));
};

export const createTreeNode = (
  group: RawGroup,
  groupTypes: GroupTypesLookup,
  parentNode?: GroupTreeNode | VirtualRoot
): GroupTreeNode => {
  const { id, name, type } = group;
  const isParentType = canHaveChildren(type, groupTypes);
  const isLoaded = isParentType ? has(group, "childTeams") : true;

  return {
    id,
    name,
    group,
    active: false,
    toggled: false,
    isParentType,
    loading: !isLoaded,
    children: null,
    ...(parentNode !== undefined ? { parentNode } : {}),
  };
};

export const addToChildren = (
  parent: GroupTreeNode | VirtualRoot,
  group: RawGroup,
  groupTypes: GroupTypesLookup,
  createTreeNodeImpl: typeof createTreeNode = createTreeNode
): GroupTreeNode => {
  const children = parent.children as Record<string, GroupTreeNode> | null;
  const { id } = group;
  const groupNode = createTreeNodeImpl(
    group,
    groupTypes,
    parent as GroupTreeNode
  );

  if (!children) {
    parent.children = { [id]: groupNode };
  } else if (!children[id]) {
    children[id] = groupNode;
  }

  return groupNode;
};

export const shouldIgnoreGroupByType = (
  type: string,
  targetGroupTypes: GroupTypesLookup | null | undefined
): boolean => {
  if (isEmpty(targetGroupTypes)) {
    return false;
  }
  return !targetGroupTypes![type];
};

const addGroupsToRoots = (
  root: GroupTreeNode | VirtualRoot,
  groups: RawGroup[],
  groupTypes: GroupTypesLookup,
  targetGroupTypes: GroupTypesLookup | null | undefined,
  createTreeNodeImpl: typeof createTreeNode
): void => {
  forEach(groups, (group) => {
    const { type } = group;
    if (shouldIgnoreGroupByType(type, targetGroupTypes)) {
      return;
    }

    const groupNode = addToChildren(
      root,
      group,
      groupTypes,
      createTreeNodeImpl
    );

    forEach(group.childTeams, (child) => {
      if (shouldIgnoreGroupByType(child.type, targetGroupTypes)) {
        return;
      }
      addToChildren(groupNode, child, groupTypes, createTreeNodeImpl);
    });
  });
};

export const convertChildrenToArray = (
  parent: GroupTreeNode | VirtualRoot,
  sortOption?: SortOption
): GroupTreeNode | VirtualRoot => {
  if (isEmpty(parent.children)) {
    const node = parent as GroupTreeNode;
    parent.children = node.isParentType && node.loading ? [] : null;
  } else {
    if (sortOption === "none") {
      parent.children = values(
        parent.children as Record<string, GroupTreeNode>
      );
    } else {
      const sortFunction: Array<(g: GroupTreeNode) => string> =
        sortOption === "groupByType" ? [byType, byName] : [byName];
      parent.children = sortBy(
        values(parent.children as Record<string, GroupTreeNode>),
        sortFunction
      );
    }

    (parent.children as GroupTreeNode[]).forEach((child) =>
      convertChildrenToArray(child, sortOption)
    );
  }

  return parent;
};

export const buildTree = (
  groups: RawGroup[],
  groupTypes: GroupTypesLookup,
  sortOption: SortOption,
  targetGroupTypes: GroupTypesLookup | null | undefined,
  createTreeNodeImpl: typeof createTreeNode,
  convertChildrenToArrayImpl: typeof convertChildrenToArray = convertChildrenToArray,
  addGroupsToRootsImpl: typeof addGroupsToRoots = addGroupsToRoots
): VirtualRoot => {
  const virtualRoot: VirtualRoot = {
    active: false,
    toggled: false,
    children: null,
  };

  addGroupsToRootsImpl(
    virtualRoot,
    groups,
    groupTypes,
    targetGroupTypes,
    createTreeNodeImpl
  );
  convertChildrenToArrayImpl(virtualRoot, sortOption);

  return virtualRoot;
};

export const addChildTeamsToNode = (
  group: RawGroup | undefined | null,
  groupNode: GroupTreeNode,
  groupTypes: GroupTypesLookup,
  sortOption?: SortOption,
  targetGroupTypes?: GroupTypesLookup | null,
  createTreeNodeImpl: typeof createTreeNode = createTreeNode,
  convertChildrenToArrayImpl: typeof convertChildrenToArray = convertChildrenToArray,
  addGroupsToRootsImpl: typeof addGroupsToRoots = addGroupsToRoots
): void => {
  if (!group) {
    return;
  }

  const { childTeams } = group;

  if (isEmpty(childTeams)) {
    groupNode.children = null;
    groupNode.isParentType = false;
    groupNode.loading = false;
    return;
  }

  groupNode.children = {};
  addGroupsToRootsImpl(
    groupNode,
    childTeams!,
    groupTypes,
    targetGroupTypes,
    createTreeNodeImpl
  );

  convertChildrenToArrayImpl(groupNode, sortOption);
  groupNode.loading = false;
};

export const closeTreeNodes = (root: GroupTreeNode | VirtualRoot): void => {
  const expandedNodes = filter(
    root.children as GroupTreeNode[],
    isBranchExpanded
  ) as GroupTreeNode[];

  if (isEmpty(expandedNodes)) {
    return;
  }

  forEach(expandedNodes, (node) => {
    node.toggled = false;
    node.active = false;
    closeTreeNodes(node);
  });
};

export const expandTreeNodes = (
  root: GroupTreeNode | VirtualRoot,
  ids: string[],
  active = true
): GroupTreeNode | null => {
  let parent = root;
  let activeNode: GroupTreeNode | null = null;

  parent.toggled = true;

  if (parent.children && !isEmpty(ids)) {
    for (let i = 0; i < ids.length; i += 1) {
      const node = find(parent.children as GroupTreeNode[], ["id", ids[i]]);

      if (node) {
        node.toggled = true;
        node.active = active;
        (parent as GroupTreeNode).active = false;
        activeNode = node;
        parent = node;
      }
    }
  }

  return activeNode;
};

export const reopenTreeToNodePaths = (
  root: GroupTreeNode | VirtualRoot,
  nodePaths: string[][]
): void => {
  closeTreeNodes(root);
  forEach(nodePaths, (path) => expandTreeNodes(root, path));
};

const clearActiveNodes = (root: GroupTreeNode | VirtualRoot): void => {
  (root as GroupTreeNode).active = false;
  forEach(root.children as GroupTreeNode[], clearActiveNodes);
};

export const reopenTreeToGroupNode = (
  root: GroupTreeNode | VirtualRoot,
  ids: string[]
): GroupTreeNode | null => {
  clearActiveNodes(root);
  return expandTreeNodes(root, ids);
};

export const findTreeNodes = (
  root: GroupTreeNode | VirtualRoot,
  ids: string[]
): GroupTreeNode[] => {
  let parent = root;
  const nodes: GroupTreeNode[] = [];

  if (parent.children && !isEmpty(ids)) {
    for (let i = 0; i < ids.length; i += 1) {
      const node = find(parent.children as GroupTreeNode[], ["id", ids[i]]);

      if (node) {
        nodes.push(node);
        parent = node;
      }
    }
  }

  return nodes;
};

export const insertChildrenNodes = (
  root: GroupTreeNode | VirtualRoot,
  ids: string[],
  groups: RawGroup[],
  groupTypes: GroupTypesLookup,
  sortOption?: SortOption,
  createTreeNodeImpl: typeof createTreeNode = createTreeNode,
  convertChildrenToArrayImpl: typeof convertChildrenToArray = convertChildrenToArray
): void => {
  let parent = root;

  if (parent.children && !isEmpty(ids)) {
    for (let i = 0; i < ids.length; i += 1) {
      const node = find(parent.children as GroupTreeNode[], ["id", ids[i]]);

      if (!node) {
        return;
      }

      if (node.loading) {
        const group = find(groups, ["id", node.id]);
        if (group) {
          addChildTeamsToNode(
            group,
            node,
            groupTypes,
            sortOption,
            null,
            createTreeNodeImpl,
            convertChildrenToArrayImpl
          );
        }
      }

      parent = node;
    }
  }
};

const EmptyTree: TreeStructureResult = {
  root: {} as VirtualRoot,
  treeData: [],
  activeNode: null,
};

export const buildTreeStructure = (
  groups: RawGroup[] | null | undefined,
  groupTypes: GroupTypesLookup,
  ids?: string[],
  sortOption?: SortOption,
  targetGroupTypes?: GroupTypesLookup | null,
  createTreeNodeImpl?: typeof createTreeNode,
  convertChildrenToArrayImpl?: typeof convertChildrenToArray,
  addGroupsToRootsImpl?: typeof addGroupsToRoots
): TreeStructureResult => {
  if (isEmpty(groups)) {
    return EmptyTree;
  }

  const root = buildTree(
    groups!,
    groupTypes,
    sortOption,
    targetGroupTypes,
    createTreeNodeImpl ?? createTreeNode,
    convertChildrenToArrayImpl,
    addGroupsToRootsImpl
  );

  const treeData = root.children as GroupTreeNode[] | null;

  closeTreeNodes(root);

  let activeNode: GroupTreeNode | null | undefined;

  if (!isEmpty(ids)) {
    activeNode = expandTreeNodes(root, ids!);
  } else if (!isEmpty(treeData) && treeData!.length === 1) {
    treeData![0]!.toggled = true;
  }

  return { root, treeData, groups: groups!, activeNode };
};

export const buildTreeFromGroups = (
  groups: RawGroup[],
  sortOption?: SortOption,
  convertChildrenToArrayImpl: typeof convertChildrenToArray = convertChildrenToArray
): GroupTreeNode[] => {
  const root: Record<string, GroupTreeNode> = {};

  forEach(groups, (group) => {
    const { id, name, parentGroups = [] } = group;

    let treeParent: Record<string, GroupTreeNode> = root;
    let parentNode: GroupTreeNode | undefined;

    forEach(parentGroups, (p) => {
      const { id: parentId, name: parentName } = p;

      if (treeParent[parentId]) {
        parentNode = treeParent[parentId];
        treeParent = treeParent[parentId]!.children as Record<
          string,
          GroupTreeNode
        >;
        return;
      }

      const node: GroupTreeNode = {
        id: parentId,
        name: parentName,
        active: false,
        toggled: true,
        loading: false,
        children: {},
        group: p as RawGroup,
        isParentType: true,
        ...(parentNode !== undefined ? { parentNode } : {}),
      };

      treeParent[parentId] = node;
      treeParent = node.children as Record<string, GroupTreeNode>;
      parentNode = node;
    });

    const existingNode = treeParent[id] ?? {};

    treeParent[id] = {
      id,
      name,
      active: true,
      toggled: true,
      loading: false,
      children: {},
      group,
      isParentType: false,
      ...existingNode,
      ...(parentNode !== undefined ? { parentNode } : {}),
    };
  });

  const tree = convertChildrenToArrayImpl(
    { children: root } as VirtualRoot,
    sortOption
  );
  return (tree?.children as GroupTreeNode[]) ?? [];
};
